/**
 * mappers/buildHedgeFundViewModel.ts — MER Algo Footprints v7.1H
 *
 * Transforms existing engine outputs into a HedgeFundModeViewModel.
 * No engine logic here — pure data reshaping for the display layer.
 */

import { SymbolContext }           from '../../../domains/market/services/buildSymbolContext';
import { SymbolThesis }            from '../../../domains/ai/thesis/thesisEngine';
import { LiveGammaSnapshot }       from '../../gamma/useGammaSnapshot';
import {
  HedgeFundModeViewModel, HFBias, HFState, HFTimeSensitivity,
  HFIntegrity, HFTrapStatus, HFGammaInfluence, HFGammaPosture,
  emptyViewModel,
} from '../types/hedgeFundViewModel';
import { STATE_COLOR, STATE_ACTION_LABEL } from '../../../domains/market/engines/stateTransition';

// ─── Decision header ──────────────────────────────────────────────────────────

function mapBias(thesis: SymbolThesis | null): HFBias {
  const bias = thesis?.current?.overallBias;
  if (bias === 'bullish') return 'bullish';
  if (bias === 'bearish') return 'bearish';
  return 'neutral';
}

function mapState(thesis: SymbolThesis | null, ctx: SymbolContext | null): HFState {
  if (!ctx || !thesis?.current) return 'avoid';
  // Prefer state transition layer when available — it has richer context
  const st = ctx.stateTransition;
  if (st) {
    if (st.actionability === 'actionable') return 'ready';
    if (st.actionability === 'pending')    return 'watch';
    return 'avoid';
  }
  // Fallback to original logic
  const { shouldTradeNow, actionability, conviction } = thesis.current;
  if (!ctx.setupActionable) return 'avoid';
  if (shouldTradeNow && actionability >= 60 && conviction >= 55) return 'ready';
  if (actionability >= 40 || ctx.bestActionableSetup) return 'watch';
  return 'avoid';
}

function mapTimeSensitivity(thesis: SymbolThesis | null): HFTimeSensitivity {
  const urgency = thesis?.current?.urgency;
  if (urgency === 'high')   return 'immediate';
  if (urgency === 'medium') return 'developing';
  const hz = thesis?.current?.timeHorizon;
  if (hz === 'next_15m') return 'immediate';
  return 'none';
}

// ─── Structure ────────────────────────────────────────────────────────────────

function mapIntegrity(ctx: SymbolContext | null): HFIntegrity {
  if (!ctx?.smc) return 'unknown';
  const strength = ctx.smc.biasStrength;
  if (strength === 'major')  return 'strong';
  if (strength === 'minor')  return 'weak';
  return 'mixed';
}

// ─── Traps ────────────────────────────────────────────────────────────────────

function mapTrapStatus(ctx: SymbolContext | null): HFTrapStatus {
  if (!ctx?.traps?.length) return 'none';
  const top = ctx.traps[0];
  if (top.severity === 'High') return 'confirmed';
  if (top.severity === 'Medium') return 'developing';
  return 'none';
}

// ─── Gamma ────────────────────────────────────────────────────────────────────

function mapGammaInfluence(snap: LiveGammaSnapshot | null): HFGammaInfluence {
  if (!snap?.available) return 'unknown';
  const score = snap.gammaScore ?? 0;
  if (score >= 70) return 'high';
  if (score >= 40) return 'medium';
  return 'low';
}

function mapGammaPosture(
  snap: LiveGammaSnapshot | null,
  bias: HFBias
): HFGammaPosture {
  if (!snap?.available) return 'unavailable';
  const regime = snap.regime;
  if (!regime) return 'neutral';
  if (bias === 'bullish' && regime === 'POSITIVE') return 'supportive';
  if (bias === 'bearish' && regime === 'NEGATIVE') return 'supportive';
  if (bias === 'bullish' && regime === 'NEGATIVE') return 'opposing';
  if (bias === 'bearish' && regime === 'POSITIVE') return 'opposing';
  return 'neutral';
}

function gammaNote(snap: LiveGammaSnapshot | null): string {
  if (!snap?.available) return 'Options data unavailable';
  const parts: string[] = [];
  if (snap.callWall)        parts.push(`Call wall ${snap.callWall}`);
  if (snap.putWall)         parts.push(`Put wall ${snap.putWall}`);
  if (snap.keyMagnetStrike) parts.push(`Magnet ${snap.keyMagnetStrike}`);
  return parts.join(' · ') || snap.notes?.[0] || 'No key levels';
}

// ─── Execution plan ───────────────────────────────────────────────────────────

function buildChecklist(thesis: SymbolThesis | null, ctx: SymbolContext | null): string[] {
  const list: string[] = [];
  const setup = ctx?.bestActionableSetup ?? ctx?.topDisplaySetup;
  if (thesis?.current?.confirmationNeeded)
    list.push(thesis.current.confirmationNeeded);
  if (setup?.confirmation && setup.confirmation !== thesis?.current?.confirmationNeeded)
    list.push(setup.confirmation);
  if (thesis?.current?.primaryRisk)
    list.push(`Risk: ${thesis.current.primaryRisk}`);
  return list.slice(0, 3);
}

function computeRR(setup: { t1?: string | null; invalidation?: string | null; entry?: string | null } | null | undefined): string | null {
  if (!setup) return null;
  try {
    const e   = parseFloat(setup.entry ?? '');
    const inv = parseFloat(setup.invalidation ?? '');
    const t1  = parseFloat(setup.t1 ?? '');
    if (!e || !inv || !t1 || isNaN(e) || isNaN(inv) || isNaN(t1)) return null;
    const risk   = Math.abs(e - inv);
    const reward = Math.abs(t1 - e);
    if (risk === 0) return null;
    return `${(reward / risk).toFixed(1)}R`;
  } catch { return null; }
}

// ─── Main builder ─────────────────────────────────────────────────────────────

export function buildHedgeFundViewModel(
  symbol:  string,
  ctx:     SymbolContext | null,
  thesis:  SymbolThesis | null,
  gamma:   LiveGammaSnapshot | null,
): HedgeFundModeViewModel {
  if (!ctx) return emptyViewModel(symbol);

  const ai    = thesis?.current ?? null;
  const setup = ctx.bestActionableSetup ?? ctx.topDisplaySetup ?? null;
  const trap  = ctx.traps?.[0] ?? null;
  const bias  = mapBias(thesis);

  // ── Decision ────────────────────────────────────────────────────────────────
  const state = mapState(thesis, ctx);
  const setupName = setup?.type
    ? setup.type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
    : null;

  // ── Structure ───────────────────────────────────────────────────────────────
  const smc = ctx.smc;
  const choch = smc?.lastCHOCH
    ? `${smc.lastCHOCH.dir?.toUpperCase()} CHOCH @ ${smc.lastCHOCH.price?.toFixed(2) ?? '?'}`
    : null;
  const bos = smc?.lastBOS
    ? `${smc.lastBOS.dir?.toUpperCase()} BOS @ ${smc.lastBOS.price?.toFixed(2) ?? '?'}`
    : null;

  let premiumDiscount: string | null = null;
  if (ctx.curPrice && ctx.curVWAP) {
    const pct = ((ctx.curPrice - ctx.curVWAP) / ctx.curVWAP * 100).toFixed(1);
    premiumDiscount = parseFloat(pct) >= 0 ? `+${pct}% VWAP (premium)` : `${pct}% VWAP (discount)`;
  }

  const structureSummary = ai?.smcRead ?? (
    smc ? `${smc.bias} bias · ${smc.biasStrength} structure` : 'Structure not computed'
  );

  // ── Liquidity ───────────────────────────────────────────────────────────────
  const nearestZone   = ctx.zones?.[0] ?? null;
  const nearestLevel  = nearestZone
    ? `${nearestZone.type?.toUpperCase() ?? 'ZONE'} @ ${nearestZone.price?.toFixed(2)}`
    : ctx.activeDecisionLevel
      ? `${ctx.activeDecisionLevel.label} @ ${ctx.activeDecisionLevel.price.toFixed(2)}`
      : null;

  // ── Traps ───────────────────────────────────────────────────────────────────
  const trapSummary = ai?.trapRead ?? (trap ? `${trap.type}: ${trap.reason}` : 'No active traps');

  // ── Gamma ───────────────────────────────────────────────────────────────────
  const gammaSummary = ai?.gammaRead ?? (gamma?.available
    ? `${gamma.regime} regime · ${gamma.bias ?? 'no bias'}`
    : 'Gamma unavailable');

  // ── Regime ──────────────────────────────────────────────────────────────────
  const regime = ctx.regime;
  const tradeStyleMap: Record<string, string> = {
    meanReversion: 'Mean Reversion',
    breakout:      'Breakout',
    trend:         'Trend Following',
    noTrade:       'Stand Aside',
  };
  const tradeStyle = regime?.dominant ? (tradeStyleMap[regime.dominant] ?? null) : null;

  // ── Thesis ──────────────────────────────────────────────────────────────────
  const whatChanged = ai?.whatChanged?.join(' · ') ?? '—';
  const confirmation = ai?.confirmationNeeded ?? setup?.confirmation ?? '—';
  const invalidationRule = ai?.invalidation ?? setup?.invalidation ?? '—';
  const thesisSummary = ai?.primaryThesis ?? 'No AI thesis available';

  // ── Execution ───────────────────────────────────────────────────────────────
  const rr = computeRR(setup);


  return {
    symbol,
    timestamp: new Date().toISOString(),

    decision: {
      bias,
      setupName,
      state,
      readinessScore: ai?.actionability ?? null,
      confidence:     ai?.conviction ?? setup?.confidence ?? null,
      timeSensitivity: mapTimeSensitivity(thesis),
    },

    structure: {
      summary:         structureSummary,
      choch,
      bos,
      premiumDiscount,
      integrity:       mapIntegrity(ctx),
    },

    liquidity: {
      summary:      ai?.liquidityRead ?? (nearestZone ? `${ctx.zones.length} zone(s) identified` : 'No key zones'),
      nearestLevel,
      sweepStatus:  null,   // v2 — no sweep state in current engine outputs
      likelyTarget: ctx.structuralLevel
        ? `${ctx.structuralLevel.label} @ ${ctx.structuralLevel.price.toFixed(2)}`
        : null,
    },

    trap: {
      summary: trapSummary,
      active:  !!trap,
      type:    trap?.type ?? null,
      status:  mapTrapStatus(ctx),
    },

    gamma: {
      summary:   gammaSummary,
      influence: mapGammaInfluence(gamma),
      posture:   mapGammaPosture(gamma, bias),
      note:      gammaNote(gamma),
    },

    regime: {
      summary:    ai?.regimeRead ?? (regime ? `${regime.label} · ${regime.posture}` : 'No regime data'),
      label:      regime?.label ?? null,
      confidence: regime?.confidence ?? null,
      tradeStyle,
    },

    execution: {
      entry:        setup?.entry ?? null,
      invalidation: setup?.invalidation ?? null,
      target1:      setup?.t1 ?? null,
      target2:      setup?.t2 ?? null,
      rr,
      checklist:    buildChecklist(thesis, ctx),
    },

    thesis: {
      summary:          thesisSummary,
      whatChanged,
      confirmation,
      invalidationRule,
    },

    review: {
      snapshotId:            null,   // wired in page from snapshot state
      outcomeStatus:         null,
      aiReviewAvailable:     !!ai,
      journalDraftAvailable: false,
    },

    // v7.1H: State Transition Layer — explicit market state for operator
    stateTransition: (() => {
      const st = ctx.stateTransition;
      return {
        state:            st.state,
        actionability:    st.actionability,
        directionalBias:  st.directionalBias,
        setupQuality:     st.setupQuality,
        confidence:       st.confidence,
        // Carried through verbatim. The band is computed once, upstream.
        effectiveConfidenceBand: st.effectiveConfidenceBand ?? null,
        rationale:        st.rationale,
        invalidation:     st.invalidation,
        longTrigger:      st.triggers.longTrigger  ?? null,
        shortTrigger:     st.triggers.shortTrigger ?? null,
        confirmation:     st.triggers.confirmation ?? [],
        failure:          st.triggers.failure      ?? [],
        contextFlags:     st.contextFlags,
        operatorGuidance: st.operatorGuidance,
        stateColor:       STATE_COLOR[st.state] ?? '#3a4558',
        actionLabel:      STATE_ACTION_LABEL[st.state] ?? 'WAIT',
      };
    })(),
  };
}
